// ============================================================
// Modal.js
// Shared modal component built on the native HTML <dialog>
// element. Phase 4 introduces it for the Issue invitation flow,
// but it's intentionally generic so future modules (Client
// Management, etc.) reuse the same pattern.
//
// Native <dialog>.showModal() handles the load-bearing accessibility
// behaviour for free:
//   - focus trap (Tab / Shift+Tab cycle within dialog)
//   - Escape key to close
//   - aria-modal semantics
//   - returning focus to the element that opened the dialog
//   - the ::backdrop pseudo-element for the scrim
//
// What this wrapper adds on top of native:
//   - React state sync between the isOpen prop and the imperative
//     showModal() / close() calls (a useEffect bridges them)
//   - Backdrop-click-to-close (native <dialog> does NOT do this)
//   - The native 'close' event (fired on Escape) is forwarded through
//     onClose so React state stays consistent
//
// Browser support: Chrome 37+, Firefox 98+, Safari 15.4+. Fine for
// the admin tool's audience.
// ============================================================

import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

// size: 'default' (500px cap — every pre-2026-08-29 consumer) or
// 'wide' (960px cap — introduced for the audit template builder,
// which edits a whole section/question tree and needs the room).
export default function Modal({ isOpen, onClose, title, closeDisabled = false, size = 'default', children }) {
  const dialogRef = useRef(null);
  const titleId = useId();

  // Sync isOpen prop → dialog imperative API. showModal() is what
  // enables the focus trap and the ::backdrop scrim — calling .open
  // = true would NOT, so we go through the methods. dialog.open is
  // the native readonly bool, used to avoid double-calls.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Native Escape closes the dialog directly via showModal()'s
  // built-in handler — bypassing our isOpen state. The 'close' event
  // fires whenever the dialog closes (Escape, .close() call, form
  // submit method=dialog). Forwarding to onClose keeps React state in
  // sync. The cycle when React drives the close is benign:
  // setState(false) → effect calls dialog.close() → 'close' event →
  // onClose() → setState(false) again, idempotent.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handler = () => onClose();
    dialog.addEventListener('close', handler);
    return () => dialog.removeEventListener('close', handler);
  }, [onClose]);

  // The dialog element itself fills the visual backdrop area; the
  // visible card is .modal__container nested inside. So a click whose
  // target is the dialog itself (not any child) is a backdrop click.
  function handleBackdropClick(e) {
    if (e.target === dialogRef.current) onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      className={size === 'wide' ? 'modal modal--wide' : 'modal'}
      onClick={handleBackdropClick}
      aria-labelledby={titleId}
    >
      <div className="modal__container">
        <header className="modal__header">
          <h2 id={titleId} className="modal__title">{title}</h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>
        <div className="modal__body">
          {children}
        </div>
      </div>
    </dialog>
  );
}
